import { useState, useCallback, useMemo, useContext } from 'react';
import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { AuthContext } from '../../state/auth';

interface ClientService {
  id: number;
  name: string;
  clinical: boolean;
  ccgCommissioned: boolean;
  disabled: boolean;
  active: boolean;
  serviceIdentifier: null; // TODO: Figure out type
  serviceName: string;
  ccgName: string;
  status: string; // TODO: Narrow to valid status values
  hasBookings: boolean;
  defaultInternalReference: string;
}

const emptyList = [];

const useClientServices = (
  clientParam = undefined,
  status = undefined,
  onlyActive = true
) => {
  const [clientServices, setClientServices] = useState<ClientService[]>([]);

  const apiRequest = useApiRequest();

  const {
    currentClient,
    bookableServices: bookableServiceIds = emptyList,
    actions: { refreshAuthToken = () => {} } = {},
  } = useContext(AuthContext);

  const bookableServices = useMemo(
    () =>
      clientParam && clientParam !== currentClient
        ? []
        : clientServices.filter(({ id }) => bookableServiceIds.includes(id)),
    [clientParam, currentClient, clientServices, bookableServiceIds]
  );

  const client = clientParam || currentClient;

  const requestValid = !!client;

  const clearData = useCallback(() => {
    setClientServices([]);
  }, []);

  const fetchData = useCallback(async () => {
    const {
      data: { clientServices: services = [] },
    } = (await apiRequest.get(
      `/v1/clients/client_services?client_id=${client}`,
      {
        params: { status, onlyActive },
      }
    )) as { data: { clientServices: ClientService[] } };

    setClientServices(services);
  }, [apiRequest, client, status, onlyActive]);

  const add = useCallback(
    async services => {
      if (!requestValid) throw new Error('Not logged in');

      const { data: clientServices } = (await apiRequest.post(
        `/v1/clients/client_services?client_id=${currentClient}`,
        {
          services,
        }
      )) as { data: ClientService[] };

      setClientServices(prev => [...prev, ...clientServices]);

      refreshAuthToken();

      return clientServices;
    },
    [apiRequest, currentClient, requestValid, refreshAuthToken]
  );

  const update = useCallback(
    async clientService => {
      if (!requestValid) throw new Error('Not logged in');

      const { data: host } = await apiRequest.patch(
        `/v1/clients/client_services/${clientService.id}?client_id=${client}`,
        {
          clientService,
        }
      );

      await fetchData();

      return host;
    },
    [apiRequest, requestValid, fetchData, client]
  );

  const destroy = useCallback(
    async clientService => {
      if (!requestValid) throw new Error('Not logged in');

      await apiRequest.delete(
        `/v1/clients/client_services/${clientService.id}?client_id=${client}`
      );

      await fetchData();
    },
    [apiRequest, requestValid, fetchData, client]
  );

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return {
    data: clientServices,
    bookableServices,
    loading,
    error,
    refresh,
    add,
    update,
    destroy,
  };
};

export default useClientServices;
